/**
 * Background events that shade the minimum-notice window on the
 * manual calendar (e.g. "no interviews can be booked in the next 2h").
 *
 * Keeps the visible range tight so we don't ship a giant background
 * event: the overlay spans from the visible week start up to
 * `now + noticeHours`, bounded by the visible week end.
 */
import { useMemo } from 'react';
import type { EventInput } from '@fullcalendar/core';
import { BG_EVENT_CLASSES } from '../events/eventStyles';
import { buildMinimumNoticeBlockIntervals } from '../../availability';

export interface UseMinimumNoticeOverlayArgs {
  noticeHours: number;
  timezone: string;
  visibleWeekStartIso: string;
  visibleWeekEndIso: string;
}

export function useMinimumNoticeOverlay({
  noticeHours,
  timezone,
  visibleWeekStartIso,
  visibleWeekEndIso,
}: UseMinimumNoticeOverlayArgs): EventInput[] {
  return useMemo(() => {
    const intervals = buildMinimumNoticeBlockIntervals({
      noticeHours,
      timezone,
      windowStart: visibleWeekStartIso,
      windowEnd: visibleWeekEndIso,
    });
    return intervals.map((iv, idx) => ({
      id: `minimum-notice-${idx}`,
      start: iv.start,
      end: iv.end,
      display: 'background' as const,
      classNames: BG_EVENT_CLASSES.minimumNotice,
      color: 'transparent',
    }));
  }, [noticeHours, timezone, visibleWeekStartIso, visibleWeekEndIso]);
}
